<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Tooltip from '@/components/molecules/Tooltip.vue'

interface Props {
  count: number
  context?: string
  variant?: 'warning' | 'danger' | 'info'
}

const props = withDefaults(defineProps<Props>(), {
  context: undefined,
  variant: 'warning',
})

// Tooltip has a multi-root template, so fallthrough attrs (e.g. data-testid)
// would be silently dropped. Forward them onto the visible span instead.
defineOptions({ inheritAttrs: false })

const isPulsing = ref(false)
const previousCount = ref(props.count)

watch(
  () => props.count,
  (newCount, oldCount) => {
    if (newCount > oldCount) {
      isPulsing.value = true
      setTimeout(() => {
        isPulsing.value = false
      }, 600)
    }
    previousCount.value = newCount
  }
)

const variantClass = computed(() => {
  if (props.variant === 'danger') {
    return 'signal-badge--error'
  }
  return `signal-badge--${props.variant}`
})

// Past two digits the badge would outgrow the control it sits on; the aria-label keeps the exact count.
const MAX_DISPLAYED_COUNT = 99

const displayedCount = computed(() =>
  props.count > MAX_DISPLAYED_COUNT ? `${MAX_DISPLAYED_COUNT}+` : String(props.count)
)

const ariaLabel = computed(() => {
  const countText = props.count === 1 ? '1 signaal' : `${props.count} signalen`
  return props.context ? `${countText} voor ${props.context}` : countText
})
</script>

<template>
  <Tooltip v-if="count > 0" :text="context ?? ''">
    <span
      v-bind="$attrs"
      :class="['signal-badge', variantClass, { 'signal-badge--pulse': isPulsing }]"
      :aria-label="ariaLabel"
      role="status"
    >
      {{ displayedCount }}
    </span>
  </Tooltip>
</template>

<style scoped>
.signal-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--spacing-xs);
  border-radius: var(--radius-full);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  color: var(--color-common-white);
}

/*
 * The badge is 12px bold white on a solid fill, so WCAG AA asks 4.5:1 of every variant.
 * `--color-semantic-warning` (#ED8B00) reaches 2.53:1 and failed axe on every page carrying
 * an open warning signal (#4154); `--color-semantic-warning-strong` is the darkened warning
 * that carries white text at 5.5:1. The error (5.17:1) and info (4.59:1) fills already pass.
 */
.signal-badge--warning {
  background-color: var(--color-semantic-warning-strong);
}

.signal-badge--error {
  background-color: var(--color-semantic-error);
}

.signal-badge--info {
  background-color: var(--color-semantic-info);
}

.signal-badge--pulse {
  animation: pulse 0.6s ease-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 currentColor;
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px transparent;
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 transparent;
  }
}
</style>
